import { useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { GainBadge } from "./GainBadge";
import { usePumpPortalConfigRevision } from "@/hooks/usePumpPortalConfigRevision";
import type { PumpPortalLiveRow } from "@/hooks/usePumpPortalTrades";
import { formatMcUsdBook, formatSol } from "@/lib/formatUsd";
import { getPumpPortalWsMode } from "@/lib/pumpPortalConfig";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { AmmDepthChart } from "./AmmDepthChart";

type ConnState = "idle" | "connecting" | "open" | "closed" | "error";

const axTheme = {
  frame:
    "rounded-xl border border-cyan-400/25 bg-gradient-to-b from-[#12122a]/95 via-[#0c0c18] to-[#06060f] shadow-[0_0_0_1px_rgba(168,85,247,0.08),inset_0_1px_0_0_rgba(34,211,238,0.06)]",
};

function fmtPrintTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function LaserstreamOrderBook({
  rows,
  state,
  error,
  yMcCap: _yMcCap,
  hideHints,
}: {
  rows: PumpPortalLiveRow[];
  state: ConnState;
  error: string | null;
  yMcCap: boolean;
  hideHints?: boolean;
}) {
  void _yMcCap;
  const { t } = useTranslation();

  usePumpPortalConfigRevision();
  const wsMode = getPumpPortalWsMode();
  const orderBookConfigHints =
    state === "open" && rows.length === 0 && !error
      ? wsMode === "anonymous"
        ? [
            "PumpPortal expects an API key for token trades. Paste your key in Setup (key icon), then Save — or set VITE_PUMPPORTAL_API_KEY in .env.local and restart.",
          ]
        : wsMode === "public-only"
          ? [
              "Public websocket mode (VITE_PUMPPORTAL_WS_PUBLIC_ONLY): prints are often missing. Unset it and use an API key for a full order book.",
            ]
          : wsMode === "api-key"
            ? [
                "Connected but no fills yet — not always wallet-related: wrong mint, PumpPortal-side limits, or a new JSON wrapper we don’t parse yet. Check red errors above; Setup has a websocket debug toggle.",
              ]
            : []
      : [];

  if (hideHints) {
    orderBookConfigHints.length = 0;
  }

  const statusKey =
    state === "open"
      ? "status.live"
      : state === "connecting"
        ? "status.conn"
        : state === "closed"
          ? "status.reconn"
          : state === "error"
            ? "status.err"
            : "status.off";

  // Calculate statistics
  const stats = useMemo(() => {
    if (rows.length === 0) return null;
    const buys = rows.filter(r => r.buy);
    const sells = rows.filter(r => !r.buy);
    const totalVol = rows.reduce((sum, r) => sum + r.sol, 0);
    const buyVol = buys.reduce((sum, r) => sum + r.sol, 0);
    const sellVol = sells.reduce((sum, r) => sum + r.sol, 0);
    const buyPct = totalVol > 0 ? (buyVol / totalVol) * 100 : 0;
    const latestMc = rows[rows.length - 1]?.mcUsd;
    return {
      total: rows.length,
      buys: buys.length,
      sells: sells.length,
      totalVol,
      buyVol,
      sellVol,
      buyPct,
      latestMc,
    };
  }, [rows]);

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 28, // ~28px height per row
    overscan: 10,
  });

  return (
    <div className={`flex min-h-[320px] min-w-0 flex-1 flex-col overflow-hidden ${axTheme.frame}`}>
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-cyan-400/15 px-3 py-2">
        <div>
          <div className="bg-gradient-to-r from-teal-200 via-cyan-200 to-violet-200 bg-clip-text text-[15px] font-semibold tracking-tight text-transparent">
            {t("orderbook.title")}
          </div>
          <p className="mt-0.5 max-w-[32rem] text-[11px] leading-snug text-violet-200/45">
            {t("orderbook.subtitle")}
          </p>
        </div>
        <span className="shrink-0 rounded-md border border-teal-400/30 bg-teal-500/10 px-2 py-0.5 font-mono text-[10px] font-medium tracking-wide text-teal-200/90">
          {t(statusKey)}
        </span>
      </div>

      <AmmDepthChart currentMcUsd={stats?.latestMc ?? undefined} />
      {error ? (
        <div className="shrink-0 px-3 py-2 text-[13px] text-red-300/95">{error}</div>
      ) : null}
      {orderBookConfigHints.map((hint, i) => (
        <div
          key={`order-book-hint-${i}`}
          className="shrink-0 border-b border-amber-400/20 bg-amber-500/10 px-3 py-2 text-[11px] leading-snug text-amber-100/90"
        >
          {hint}
        </div>
      ))}

      {rows.length > 0 && stats ? (
        <div className="shrink-0 border-b border-cyan-400/10 bg-[#0a0a14]/50 px-3 py-2">
          <div className="grid grid-cols-4 gap-3">
            <div className="flex items-center gap-1.5">
              <Activity className="size-3 text-cyan-400" />
              <div>
                <div className="text-[8px] text-white/30 uppercase tracking-wider">{t("orderbook.total")}</div>
                <div className="text-[11px] font-mono font-bold text-white">{stats.total}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <TrendingUp className="size-3 text-emerald-400" />
              <div>
                <div className="text-[8px] text-white/30 uppercase tracking-wider">{t("orderbook.buys")}</div>
                <div className="text-[11px] font-mono font-bold text-emerald-400">{stats.buys}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <TrendingDown className="size-3 text-rose-400" />
              <div>
                <div className="text-[8px] text-white/30 uppercase tracking-wider">{t("orderbook.sells")}</div>
                <div className="text-[11px] font-mono font-bold text-rose-400">{stats.sells}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="text-[8px] text-white/30 uppercase tracking-wider">{t("orderbook.buyPct")}</div>
              <div className={`text-[11px] font-mono font-bold ${stats.buyPct >= 60 ? 'text-emerald-400' : stats.buyPct <= 40 ? 'text-rose-400' : 'text-white'}`}>
                {(stats.buyPct ?? 0).toFixed(0)}%
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-2">
            <div>
              <div className="text-[8px] text-white/30 uppercase tracking-wider">{t("orderbook.volume")}</div>
              <div className="text-[11px] font-mono text-cyan-300">{formatSol(stats.totalVol)} SOL</div>
            </div>
            <div>
              <div className="text-[8px] text-white/30 uppercase tracking-wider">{t("orderbook.buyVol")}</div>
              <div className="text-[11px] font-mono text-emerald-300/80">{formatSol(stats.buyVol)} SOL</div>
            </div>
            <div>
              <div className="text-[8px] text-white/30 uppercase tracking-wider">{t("orderbook.sellVol")}</div>
              <div className="text-[11px] font-mono text-rose-300/80">{formatSol(stats.sellVol)} SOL</div>
            </div>
          </div>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="min-h-0 flex-1 overflow-hidden bg-[#0a0a14]/90 px-2 py-2">
          <div className="mb-1.5 flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-200/60">
              {t("orderbook.recentPrints", { count: rows.length })}
            </div>
            <div className="text-[8px] animate-casino-flash font-bold flex items-center">
              {stats?.latestMc ? `MC: ${formatMcUsdBook(stats.latestMc)}` : ''}
              {stats?.latestMc && (stats as any)?.ath ? ` ✨ ATH: ${formatMcUsdBook((stats as any).ath)}` : ''}
              <GainBadge currentMc={stats?.latestMc} spawnMc={(stats as any)?.spawnMc} priceChange24h={(stats as any)?.priceChange24h} />
            </div>
          </div>
          <div ref={parentRef} className="max-h-[min(60vh,500px)] min-h-[200px] overflow-y-auto font-mono text-[11px]">
            <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const r = rows[virtualRow.index];
                return (
                  <div
                    key={r.id}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className="flex justify-between gap-2 border-b border-white/[0.04] py-1 text-violet-200/90 hover:bg-white/5 transition-colors"
                  >
                    <span className="shrink-0 text-violet-400/70 w-16">{fmtPrintTime(r.ts)}</span>
                    <span className={`shrink-0 w-12 font-medium ${r.buy ? "text-emerald-300" : "text-rose-300"}`}>
                      {r.buy ? t("orderbook.buy") : t("orderbook.sell")}
                    </span>
                    <span className="text-cyan-100/80 w-20 text-right">{formatSol(r.sol)}</span>
                    <span className="shrink-0 text-right text-violet-300/80 w-24">
                      {r.mcUsd != null ? formatMcUsdBook(r.mcUsd) : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : state === "open" && !error ? (
        <div className="flex min-h-[200px] flex-1 items-center justify-center px-3 py-6 text-center text-[11px] leading-relaxed text-violet-300/45">
          {t("orderbook.waiting")}
        </div>
      ) : null}
    </div>
  );
}
